import React, { useState, useRef, useEffect } from "react";
import "./ChatbotWidget.css";
import caniImage from "../assets/cani_laugh.png";
import loboImage from "../assets/lobo_laugh.png";
import wolfdogImage from "../assets/wolfdog_happy.png";

const faqLink = "https://documents.conquerlife.io/faq";
const termsLink = "https://documents.conquerlife.io/terms-and-conditions";
const privacyLink = "https://documents.conquerlife.io/privacy-policy";
const CommunityGuidelines = "https://www.conquerlife.io/community-guidelines";

// Character configurations
const CHARACTERS = [
  {
    name: "Cani",
    image: caniImage,
    greeting: "Hi! I'm Cani, your friendly assistant!",
  },
  {
    name: "Lobo",
    image: loboImage,
    greeting: "Hey there! I'm Lobo, here to help you out!",
  },
  {
    name: "Wolfdog",
    image: wolfdogImage,
    greeting: "Hello! I'm Wolfdog, ready to assist you!",
  },
];

const ChatbotWidget = () => {
  // Select a random character on component mount
  const [selectedCharacter] = useState(() => {
    const randomIndex = Math.floor(Math.random() * CHARACTERS.length);
    return CHARACTERS[randomIndex];
  });

  const [isOpen, setIsOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: selectedCharacter.greeting,
      timestamp: new Date(),
      showActions: true,
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API_URL =
    "https://alb.api-dev-conquerlifeco.com/api/chatbot/ask-question";

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleChatWithMe = () => {
    setShowQuickActions(false);

    const assistantMessage = {
      role: "assistant",
      content: "Great! How may I help you? Feel free to ask me anything!",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, assistantMessage]);
  };

  const handleOpenLink = (url, linkName) => {
    window.open(url, "_blank", "noopener,noreferrer");

    const userMessage = {
      role: "user",
      content: `Opened ${linkName}`,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
  };

  const sendMessage = async (e) => {
    e.preventDefault();

    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages
        .filter((msg) => msg.role !== "system")
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: inputValue.trim(),
          conversationHistory: conversationHistory,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const text = await response.text();

      console.log("response: ", response);

      const assistantMessage = {
        role: "assistant",
        content: text,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error sending message:", error);

      const errorMessage = {
        role: "assistant",
        content:
          "Sorry, I encountered an error. Please try again or contact support at support@conquerlife.io",
        timestamp: new Date(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format message content to handle bullet points and line breaks
  const formatMessageContent = (content) => {
    if (!content) return "";

    // Split by lines
    const lines = content.split("\n");

    return lines.map((line, index) => {
      // Check if line starts with bullet points or numbers
      const trimmedLine = line.trim();

      if (trimmedLine === "") {
        return <br key={index} />;
      }

      // Handle different bullet point styles
      if (trimmedLine.match(/^[-•*]\s/)) {
        return (
          <div key={index} style={{ marginLeft: "12px", marginBottom: "6px" }}>
            • {trimmedLine.replace(/^[-•*]\s/, "")}
          </div>
        );
      }

      // Handle numbered lists
      if (trimmedLine.match(/^\d+\.\s/)) {
        return (
          <div key={index} style={{ marginLeft: "12px", marginBottom: "6px" }}>
            {trimmedLine}
          </div>
        );
      }

      // Regular text
      return (
        <div key={index} style={{ marginBottom: trimmedLine ? "6px" : "0" }}>
          {trimmedLine}
        </div>
      );
    });
  };

  return (
    <div className="chatbot-container">
      {/* Chat Window */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            <div className="chat-header-content">
              <div className="chat-avatar">
                <img
                  src={selectedCharacter.image}
                  alt={selectedCharacter.name}
                  className="chat-avatar-img"
                />
              </div>
              <div className="chat-title">
                <h3>{selectedCharacter.name}</h3>
                <span className="chat-status">Online</span>
              </div>
            </div>
            <button
              className="close-button"
              onClick={toggleChat}
              aria-label="Close chat"
            >
              ✕
            </button>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div key={index}>
                <div
                  className={`message ${msg.role} ${
                    msg.isError ? "error" : ""
                  }`}
                >
                  <div className="message-content">
                    <div className="formatted-text">
                      {formatMessageContent(msg.content)}
                    </div>
                    <span className="message-time">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>

                {/* Show quick action buttons only for the first message */}
                {msg.showActions && showQuickActions && index === 0 && (
                  <div className="quick-actions">
                    <button
                      className="action-button primary"
                      onClick={handleChatWithMe}
                    >
                      💬 Chat with me
                    </button>
                    <button
                      className="action-button secondary"
                      onClick={() => handleOpenLink(faqLink, "FAQ Page")}
                    >
                      📚 View FAQ Page
                    </button>
                    <button
                      className="action-button secondary"
                      onClick={() =>
                        handleOpenLink(
                          CommunityGuidelines,
                          "Community Guidelines"
                        )
                      }
                    >
                      👥 Community Guidelines
                    </button>
                    <button
                      className="action-button secondary"
                      onClick={() =>
                        handleOpenLink(termsLink, "Terms & Conditions")
                      }
                    >
                      📋 Terms & Conditions
                    </button>
                    <button
                      className="action-button secondary"
                      onClick={() =>
                        handleOpenLink(privacyLink, "Privacy Policy")
                      }
                    >
                      🔒 Privacy Policy
                    </button>
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message assistant">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-container" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              placeholder="Type your question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="send-button"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M2 10L18 2L10 18L8 11L2 10Z" fill="currentColor" />
              </svg>
            </button>
          </form>
        </div>
      )}

      {/* Floating Bubble Button */}
      <button
        className={`chat-bubble ${isOpen ? "hidden" : ""}`}
        onClick={toggleChat}
        aria-label="Open chat"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12C2 13.54 2.36 15 3 16.35V22L8.65 19C10 19.64 11.46 20 13 20C18.52 20 23 15.52 23 10C23 4.48 18.52 2 12 2Z"
            fill="white"
          />
        </svg>
        <span className="chat-bubble-badge">?</span>
      </button>
    </div>
  );
};

export default ChatbotWidget;
